import { useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight, X, Sparkles } from "lucide-react";
import { useProductTour } from "@/hooks/use-product-tour";
import { ProductTourProgress } from "./ProductTourProgress";

const TOOLTIP_MAX_WIDTH = 360;
// Hauteur FIXE de la carte : le bouton « Suivant » garde ainsi des coordonnées
// constantes d'une étape à l'autre (clic répété sans bouger la souris). Le texte
// plus long défile dans sa zone ; le footer reste collé en bas.
const TOOLTIP_HEIGHT = 340;

export function ProductTourTooltip() {
  const {
    currentStep,
    currentStepIndex,
    totalSteps,
    nextStep,
    skipTour,
  } = useProductTour();

  const isFirst = currentStepIndex === 0;
  const isLast = currentStepIndex === totalSteps - 1;
  const isCentered = currentStep?.placement === "center";

  // ─── Position : toujours au centre de l'ecran ─────────────────────────────
  // La bulle reste fixe au milieu pour toutes les etapes : c'est le spotlight
  // (trou + halo dans l'overlay) qui designe l'element cible. On evite ainsi
  // le saut de la bulle quand le rect de la cible est (re)mesure de facon
  // asynchrone — la position ne depend plus de targetRect.
  const style = useMemo(
    () => ({
      position: "fixed" as const,
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: Math.min(TOOLTIP_MAX_WIDTH, window.innerWidth - 32),
      height: Math.min(TOOLTIP_HEIGHT, window.innerHeight - 48),
    }),
    [],
  );

  if (!currentStep) return null;

  const ctaLabel = currentStep.ctaLabel ?? "Suivant";

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={currentStep.id}
        className="flex flex-col rounded-2xl border border-white/15 bg-black/70 backdrop-blur-2xl shadow-2xl p-5 max-w-[calc(100vw-2rem)]"
        style={{ ...style, pointerEvents: "auto", zIndex: 10000 }}
        initial={{ opacity: 0, y: 12, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -8, scale: 0.96 }}
        transition={{ type: "spring", stiffness: 400, damping: 30 }}
      >
        {/* Close button */}
        <button
          onClick={(e) => { e.stopPropagation(); skipTour(); }}
          className="absolute top-3 right-3 p-1 rounded-lg text-white/40 hover:text-white/80 hover:bg-white/10 transition-colors"
          aria-label="Fermer le tutoriel"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Icon for centered steps */}
        {isCentered && (
          <div className="flex justify-center mb-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-sidebar-primary/20 border border-sidebar-primary/30">
              <Sparkles className="h-7 w-7 text-sidebar-primary" />
            </div>
          </div>
        )}

        {/* Title */}
        <h3
          className={`text-base font-bold text-white mb-1.5 pr-6 ${
            isCentered ? "text-center" : ""
          }`}
        >
          {currentStep.title}
        </h3>

        {/* Description */}
        <p
          className={`flex-1 min-h-0 overflow-y-auto text-sm text-white/70 leading-relaxed mb-4 ${
            isCentered ? "text-center" : ""
          }`}
        >
          {currentStep.description}
        </p>

        {/* Footer: progress ligne 1, actions ligne 2 */}
        <div className="flex flex-col gap-2.5">
          <div className="flex items-center justify-between">
            <ProductTourProgress
              currentIndex={currentStepIndex}
              totalSteps={totalSteps}
            />
            <span className="text-3xs text-white/30 shrink-0">
              {currentStepIndex + 1} / {totalSteps}
            </span>
          </div>

          <div className="flex items-center justify-between">
            {!isFirst && !isLast ? (
              <button
                onClick={(e) => { e.stopPropagation(); skipTour(); }}
                className="px-3 py-1.5 text-xs font-medium text-white/50 hover:text-white/80 transition-colors"
              >
                Passer
              </button>
            ) : <span />}

            <button
              onClick={(e) => { e.stopPropagation(); nextStep(); }}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-sidebar-primary text-white text-sm font-semibold hover:opacity-90 transition-opacity shadow-lg shadow-sidebar-primary/25"
            >
              {ctaLabel}
              {!isLast && !isFirst && <ChevronRight className="h-3.5 w-3.5" />}
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
